/**
 * Edits as governed parameter change sets — the one composer behind every «Propose for approval»
 * that writes `POST /registry/changes` from a set of edited rows (`docs/adr/9024`, amendment of
 * 2026-11-03). The register's change tray (`src/features/foundations/parameters-v7/`) composed
 * here first; Compacting's «Propose these parameters» (`src/features/inseason/compacting/`) composes
 * a simulation's overlay through the same function, so the two cannot drift. No React, no clock.
 *
 * **One change set per row owner, never a reassigned owner.** The platform writes the SET's `owner`
 * onto every row it applies (bata-platform `services/repositories/changeset.py`
 * `_apply_parameter_item`: `"owner": change_set.owner`), so edits of rows of two owners composed into
 * one set would silently change who owns half of them. `composeParameterChangeSet` returns one set
 * per distinct owner.
 */

import type { ParameterRow, ProposeRequest, RevisionRequest } from '@/api/generated';

import { isNumericControl, type DraftCheck, type ParameterControl } from './drafts';

// ── the rationale ────────────────────────────────────────────────────────────

/** «Perché questa modifica»: the house minimum, in words. */
export const MINIMUM_RATIONALE_WORDS = 8;

export function rationaleWordCount(rationale: string): number {
  return rationale.trim().split(/\s+/).filter((word) => word !== '').length;
}

// ── «Efficace dal» ───────────────────────────────────────────────────────────

function addDaysIso(today: string, days: number): string {
  const next = new Date(`${today}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + days);
  return next.toISOString().slice(0, 10);
}

/**
 * The default «Efficace dal»: the screen's `asOf` when it is in the future — the planner is looking
 * at a future register and is proposing for it — otherwise tomorrow. Never today: a value proposed
 * today is approved later, and an effective date already passed by then is a backdated change nobody
 * chose. ISO dates compare as strings.
 */
export function defaultValidFrom(asOf: string, today: string): string {
  if (asOf > today) return asOf;
  return addDaysIso(today, 1);
}

// ── the composer ─────────────────────────────────────────────────────────────

/**
 * One edit the composer reads: the row it starts from (its scope, `validTo`, owner and review date),
 * the control that typed the value, and the draft check's verdict on it. The register's `Edit` is one.
 */
export interface ChangeSetEdit {
  readonly row: ParameterRow;
  readonly control: ParameterControl;
  readonly check: DraftCheck;
}

export type ChangeSetRefusal =
  | 'nothing_changed'
  | 'invalid_values'
  | 'no_rationale'
  | 'rationale_too_short'
  | 'no_valid_from'
  | 'no_change_id'
  | 'change_id_too_long'
  | 'no_owner'
  | 'no_review_date'
  | 'no_validity_window'
  | 'valid_from_not_before_valid_to';

export interface ChangeSetMeta {
  readonly rationale: string;
  /** «Efficace dal» — ISO date. */
  readonly validFrom: string;
  /** The decision's id; see `composeParameterChangeSet` for how N sets derive theirs. */
  readonly changeId: string;
}

export type ChangeSetComposition =
  | { readonly ok: true; readonly sets: readonly ProposeRequest[] }
  | { readonly ok: false; readonly refusal: ChangeSetRefusal };

/** bata-platform `ProposeRequest.change_id`: `max_length=64` (`registry_change_set.change_id` VARCHAR(64)). */
export const MAX_CHANGE_ID_LENGTH = 64;

/**
 * The edits as governed change sets — ONE PER DISTINCT ROW OWNER (see the module comment), or the
 * one reason none can be composed. The whole set of edits is refused rather than partly proposed: a
 * proposal of four of six edits would report success over a perimeter nobody chose.
 *
 * Each revision takes its row's own scope (`scopeDimension`, `scopeValue`), `validTo` and owner,
 * from `meta.validFrom`; its value is the check's `sent`, in `numericValue` for a numeric control and
 * `textValue` otherwise. Each set carries the same rationale and `validFrom`; its `reviewDueOn` is the
 * soonest review date among its rows.
 *
 * **changeId derivation.** One owner: `meta.changeId`, verbatim — the mutation's idempotency key
 * already identifies the decision (ADR 0016). Several owners: `<changeId>-1`, `<changeId>-2`, …
 * with owners in code-point order of their trimmed name, so the same edits compose the same ids on
 * every retry. A derived id longer than the platform's 64 is refused, not truncated.
 */
export function composeParameterChangeSet(edits: readonly ChangeSetEdit[], meta: ChangeSetMeta): ChangeSetComposition {
  if (edits.length === 0) return { ok: false, refusal: 'nothing_changed' };
  if (edits.some((edit) => !edit.check.ok)) return { ok: false, refusal: 'invalid_values' };
  if (meta.rationale.trim() === '') return { ok: false, refusal: 'no_rationale' };
  if (rationaleWordCount(meta.rationale) < MINIMUM_RATIONALE_WORDS) return { ok: false, refusal: 'rationale_too_short' };
  if (meta.validFrom.trim() === '') return { ok: false, refusal: 'no_valid_from' };
  if (meta.changeId.trim() === '') return { ok: false, refusal: 'no_change_id' };

  const byOwner = new Map<string, { revisions: RevisionRequest[]; reviewDates: string[] }>();
  for (const edit of edits) {
    if (!edit.check.ok) return { ok: false, refusal: 'invalid_values' };
    const { row } = edit;
    const owner = row.owner.trim();
    if (owner === '') return { ok: false, refusal: 'no_owner' };
    if (row.reviewDate.trim() === '') return { ok: false, refusal: 'no_review_date' };
    if (row.validTo.trim() === '') return { ok: false, refusal: 'no_validity_window' };
    // ISO dates compare exactly as strings.
    if (meta.validFrom >= row.validTo) return { ok: false, refusal: 'valid_from_not_before_valid_to' };
    const numeric = isNumericControl(edit.control);
    const revision: RevisionRequest = {
      parameterKey: row.parameterKey,
      scopeDimension: row.scopeDimension,
      scopeValue: row.scopeValue,
      numericValue: numeric ? Number(edit.check.sent) : null,
      textValue: numeric ? null : edit.check.sent,
      validFrom: meta.validFrom,
      validTo: row.validTo,
    };
    const bucket = byOwner.get(owner) ?? { revisions: [], reviewDates: [] };
    bucket.revisions.push(revision);
    bucket.reviewDates.push(row.reviewDate);
    byOwner.set(owner, bucket);
  }

  const owners = [...byOwner.keys()].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  const rationale = meta.rationale.trim();
  const sets: ProposeRequest[] = [];
  for (const [index, owner] of owners.entries()) {
    const bucket = byOwner.get(owner);
    if (bucket === undefined) continue;
    const changeId = owners.length === 1 ? meta.changeId : `${meta.changeId}-${index + 1}`;
    if (changeId.length > MAX_CHANGE_ID_LENGTH) return { ok: false, refusal: 'change_id_too_long' };
    sets.push({
      changeId,
      owner,
      rationale,
      reviewDueOn: [...bucket.reviewDates].sort()[0] ?? '',
      evidenceRef: null,
      revisions: bucket.revisions,
    });
  }
  return { ok: true, sets };
}
